"use client";

import { useMemo, useState } from "react";
import type { Order, Table, TableStatus } from "@/lib/pos-types";
import {
  cn,
  formatPrice,
  getTableStatusColor,
  getTableStatusLabel,
  zones as defaultZones,
} from "@/lib/utils";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Users, Clock, ChefHat, CheckCircle2 } from "lucide-react";
import { Button } from "../ui/button";

interface TablesViewProps {
  onTableSelect: (table: Table) => void;
  onTakeawaySelect: () => void;
  selectedTableId?: string | null;
  tables: Table[];
  orders: Order[];
  zones?: string[];
  pendingSyncTableIds?: string[];
}

const tableStatuses: TableStatus[] = [
  "available",
  "occupied",
  "blocked",
  "pending-payment",
];

export function TablesView({
  onTableSelect,
  onTakeawaySelect,
  selectedTableId,
  tables,
  orders,
  zones,
  pendingSyncTableIds,
}: TablesViewProps) {
  const [filter, setFilter] = useState<"all" | TableStatus>("all");
  const zonesList = zones && zones.length > 0 ? zones : defaultZones;
  const pendingSet = useMemo(
    () => new Set(pendingSyncTableIds ?? []),
    [pendingSyncTableIds],
  );
  const ALL_ZONES = "__all__";
  const zoneOptions = [ALL_ZONES, ...zonesList];
  const [activeZone, setActiveZone] = useState(ALL_ZONES);

  const effectiveZone = useMemo(
    () =>
      activeZone && zonesList.includes(activeZone)
        ? activeZone
        : zonesList[0] || "Terraza",
    [activeZone, zonesList],
  );

  const filteredTables = tables.filter((table) => {
    const statusMatch = filter === "all" || table.status === filter;

    const zoneMatch = activeZone === ALL_ZONES || table.zone === activeZone;

    return statusMatch && zoneMatch;
  });

  const minutesAgo = (date?: Date) => {
    if (!date) return null;

    const diff = Date.now() - new Date(date).getTime();

    const minutes = Math.floor(diff / 60000);

    if (minutes < 60) return `${minutes}m`;

    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;

    return `${hours}h ${mins}m`;
  };

  const groupedTables = useMemo(() => {
    const groups: Record<string, typeof filteredTables> = {};

    zonesList.forEach((zone) => {
      groups[zone] = [];
    });

    filteredTables.forEach((table) => {
      if (!groups[table.zone]) {
        groups[table.zone] = [];
      }

      groups[table.zone].push(table);
    });

    Object.values(groups).forEach((tables) =>
      tables.sort((a, b) => a.number - b.number),
    );

    return groups;
  }, [filteredTables, zonesList]);

  return (
    <div className="flex flex-col h-full gap-4 sm:gap-6">
      {/* Zones and Filters - Responsive Layout */}
      <div className="flex flex-col gap-4">
        {/* Zones - Horizontal Scroll on small screens */}
        <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1 px-1">
          {zoneOptions.map((zone) => (
            <button
              key={zone}
              onClick={() => setActiveZone(zone)}
              className={cn(
                "px-4 py-2 rounded-lg text-xs font-semibold whitespace-nowrap transition-all duration-200",
                activeZone === zone
                  ? "bg-primary text-primary-foreground shadow-lg shadow-primary/20 scale-105"
                  : "bg-card border border-border text-muted-foreground hover:text-foreground",
              )}
            >
              {zone === ALL_ZONES ? "Todas" : zone}
            </button>
          ))}
        </div>

        <div className="w-full flex justify-between">
          {/* Status Filters - Row with wrap */}

          <div className="flex flex-wrap items-center gap-2 px-1">
            <button
              onClick={() => setFilter("all")}
              className={cn(
                "px-3 py-1.5 rounded-full text-[10px] sm:text-xs font-medium border transition-colors",
                filter === "all"
                  ? "bg-foreground text-background border-foreground"
                  : "bg-transparent text-muted-foreground border-border hover:border-muted-foreground",
              )}
            >
              Todos
            </button>
            {tableStatuses.map((status) => (
              <button
                key={status}
                onClick={() => setFilter(status)}
                className={cn(
                  "px-3 py-1.5 rounded-full text-[10px] sm:text-xs font-medium border transition-colors flex items-center gap-2",
                  filter === status
                    ? "bg-foreground text-background border-foreground"
                    : "bg-transparent text-muted-foreground border-border hover:border-muted-foreground",
                )}
              >
                <div
                  className={cn(
                    "w-2 h-2 rounded-full",
                    getTableStatusColor(status).split(" ")[0],
                  )}
                />
                {getTableStatusLabel(status)}
              </button>
            ))}
          </div>


        </div>
      </div>

      {/* Tables Grid - Responsive Columns */}
      <div className="flex-1 min-h-0">
        <ScrollArea className="h-full pr-1">
          {activeZone === ALL_ZONES ? (
            <div className="space-y-8 pb-6">
              {Object.entries(groupedTables).map(([zone, zoneTables]) => (
                <div key={zone}>
                  <h2 className="text-lg font-bold mb-4 px-1">{zone}</h2>

                  <div className="grid grid-cols-2 xs:grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-3 sm:gap-4">
                    {zoneTables.map((table) => {
                      const statusColorClasses = getTableStatusColor(
                        table.status,
                      );

                      const order =
                        table.status === "occupied" ||
                        table.status === "pending-payment"
                          ? (
                              (table.currentOrderId ? orders.find((o) => o.id === table.currentOrderId) : undefined) ??
                              orders.find(
                                (o) =>
                                  o.tableId === table.id &&
                                  o.status !== "closed" &&
                                  o.status !== "paid"
                              )
                            )
                          : undefined;

                      const orderTotal = order?.total;
                      const createdAt = order?.createdAt;
                      const sentToKitchenAt = order?.sentToKitchenAt;

                      const firstDeliveredAt = order?.items
                        ?.filter((item) => item.deliveredAt)
                        .sort(
                          (a, b) =>
                            new Date(a.deliveredAt!).getTime() -
                            new Date(b.deliveredAt!).getTime(),
                        )[0]?.deliveredAt;

                      return (
                        <button
                          key={table.id}
                          onClick={() => onTableSelect(table)}
                          className={cn(
                            "relative aspect-square sm:aspect-auto sm:h-36 rounded-2xl border-2 transition-all duration-300 group overflow-hidden",
                            "flex flex-col items-center justify-center gap-2 sm:gap-3 p-3 sm:p-4",
                            selectedTableId === table.id
                              ? "border-primary bg-primary/5 shadow-xl shadow-primary/10 ring-2 ring-primary/20"
                              : "border-border bg-card hover:border-primary/50 hover:bg-muted/30",
                          )}
                        >
                          {pendingSet.has(table.id) && (
                            <div className="absolute top-2 right-2 w-2 h-2 rounded-full bg-amber-400" />
                          )}
                          {/* Status Indicator Bar */}
                          <div
                            className={cn(
                              "absolute top-0 left-0 right-0 h-1 sm:h-1.5",
                              statusColorClasses.split(" ")[0],
                            )}
                          />

                          {/* Table Icon/Number */}
                          <div
                            className={cn(
                              "w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center transition-transform duration-300 group-hover:scale-110",
                              table.status === "occupied"
                                ? "bg-amber-500/10 text-amber-500"
                                : table.status === "pending-payment"
                                  ? "bg-violet-500/10 text-violet-500"
                                  : table.status === "blocked"
                                    ? "bg-zinc-500/10 text-zinc-500"
                                    : "bg-emerald-500/10 text-emerald-500",
                            )}
                          >
                            <span className="text-sm sm:text-lg font-black">
                              {table.number}
                            </span>
                          </div>

                          {/* Info Text */}
                          <div className="text-center min-w-0 w-full">
                            <span className="text-[10px] sm:text-xs font-bold text-foreground uppercase tracking-wider block truncate">
                              Mesa {table.number}
                            </span>
                            {table.status === "occupied" ||
                            table.status === "pending-payment" ? (
                              <div className="flex flex-col items-center mt-0.5 sm:mt-1 w-full">
                                <span
                                  className={cn(
                                    "text-[9px] sm:text-[10px] font-mono font-bold px-1.5 rounded truncate w-full",
                                    table.status === "occupied"
                                      ? "text-amber-500 bg-amber-500/10"
                                      : "text-violet-500 bg-violet-500/10",
                                  )}
                                >
                                  {orderTotal !== undefined
                                    ? formatPrice(orderTotal)
                                    : "--"}
                                </span>

                                {order &&
                                  (() => {
                                    const tableTime = minutesAgo(
                                      table.openedAt,
                                    );
                                    const kitchenTime =
                                      minutesAgo(sentToKitchenAt);
                                    const deliveredTime =
                                      minutesAgo(firstDeliveredAt);

                                    if (
                                      !tableTime &&
                                      !kitchenTime &&
                                      !deliveredTime
                                    )
                                      return null;

                                    return (
                                      <div className="mt-1.5 flex flex-col gap-0.5 text-[8px] sm:text-[9px] leading-none w-full items-start px-2">
                                        {tableTime && (
                                          <div className="flex items-center gap-1 text-muted-foreground">
                                            <Clock className="w-2.5 h-2.5 shrink-0 text-muted-foreground/60" />
                                            <span>Mesa: {tableTime}</span>
                                          </div>
                                        )}
                                        {kitchenTime && (
                                          <div className="flex items-center gap-1 text-amber-500 font-medium">
                                            <ChefHat className="w-2.5 h-2.5 shrink-0 text-amber-500/70" />
                                            <span>Cocina: {kitchenTime}</span>
                                          </div>
                                        )}
                                        {deliveredTime && (
                                          <div className="flex items-center gap-1 text-emerald-500 font-medium">
                                            <CheckCircle2 className="w-2.5 h-2.5 shrink-0 text-emerald-500/70" />
                                            <span>
                                              Entregado: {deliveredTime}
                                            </span>
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })()}

                                {pendingSet.has(table.id) && (
                                  <span className="text-[9px] sm:text-[10px] text-amber-500 mt-0.5 font-medium block">
                                    Pendiente sync
                                  </span>
                                )}
                              </div>
                            ) : (
                              <>
                                <span className="text-[9px] sm:text-[10px] text-muted-foreground mt-0.5 sm:mt-1 font-medium italic block">
                                  {table.status === "blocked" &&
                                  table.blockedReason
                                    ? table.blockedReason
                                    : getTableStatusLabel(table.status)}
                                </span>
                                {pendingSet.has(table.id) && (
                                  <span className="text-[9px] sm:text-[10px] text-amber-500 mt-0.5 font-medium block">
                                    Pendiente sync
                                  </span>
                                )}
                              </>
                            )}
                          </div>

                          {/* Capacity - Tiny corner badge */}
                          <div className="absolute bottom-2 right-2 flex items-center gap-1 text-muted-foreground/40">
                            <Users className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
                            <span className="text-[8px] sm:text-[10px] font-bold">
                              {table.capacity}
                            </span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 xs:grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-3 sm:gap-4 pb-6">
              {filteredTables.map((table) => {
                const statusColorClasses = getTableStatusColor(table.status);

                const order =
                  table.status === "occupied" ||
                  table.status === "pending-payment"
                    ? orders.find((o) => o.tableId === table.id)
                    : undefined;

                const orderTotal = order?.total;
                const createdAt = order?.createdAt;
                const sentToKitchenAt = order?.sentToKitchenAt;

                const firstDeliveredAt = order?.items
                  ?.filter((item) => item.deliveredAt)
                  .sort(
                    (a, b) =>
                      new Date(a.deliveredAt!).getTime() -
                      new Date(b.deliveredAt!).getTime(),
                  )[0]?.deliveredAt;

                return (
                  <button
                    key={table.id}
                    onClick={() => onTableSelect(table)}
                    className={cn(
                      "relative aspect-square sm:aspect-auto sm:h-36 rounded-2xl border-2 transition-all duration-300 group overflow-hidden",
                      "flex flex-col items-center justify-center gap-2 sm:gap-3 p-3 sm:p-4",
                      selectedTableId === table.id
                        ? "border-primary bg-primary/5 shadow-xl shadow-primary/10 ring-2 ring-primary/20"
                        : "border-border bg-card hover:border-primary/50 hover:bg-muted/30",
                    )}
                  >
                    {pendingSet.has(table.id) && (
                      <div className="absolute top-2 right-2 w-2 h-2 rounded-full bg-amber-400" />
                    )}
                    {/* Status Indicator Bar */}
                    <div
                      className={cn(
                        "absolute top-0 left-0 right-0 h-1 sm:h-1.5",
                        statusColorClasses.split(" ")[0],
                      )}
                    />

                    {/* Table Icon/Number */}
                    <div
                      className={cn(
                        "w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center transition-transform duration-300 group-hover:scale-110",
                        table.status === "occupied"
                          ? "bg-amber-500/10 text-amber-500"
                          : table.status === "pending-payment"
                            ? "bg-violet-500/10 text-violet-500"
                            : table.status === "blocked"
                              ? "bg-zinc-500/10 text-zinc-500"
                              : "bg-emerald-500/10 text-emerald-500",
                      )}
                    >
                      <span className="text-sm sm:text-lg font-black">
                        {table.number}
                      </span>
                    </div>

                    {/* Info Text */}
                    <div className="text-center min-w-0 w-full">
                      <span className="text-[10px] sm:text-xs font-bold text-foreground uppercase tracking-wider block truncate">
                        Mesa {table.number}
                      </span>
                      {table.status === "occupied" ||
                      table.status === "pending-payment" ? (
                        <div className="flex flex-col items-center mt-0.5 sm:mt-1 w-full">
                          <span
                            className={cn(
                              "text-[9px] sm:text-[10px] font-mono font-bold px-1.5 rounded truncate w-full",
                              table.status === "occupied"
                                ? "text-amber-500 bg-amber-500/10"
                                : "text-violet-500 bg-violet-500/10",
                            )}
                          >
                            {orderTotal !== undefined
                              ? formatPrice(orderTotal)
                              : "--"}
                          </span>

                          {order &&
                            (() => {
                              const tableTime = minutesAgo(table.openedAt);
                              const kitchenTime = minutesAgo(sentToKitchenAt);
                              const deliveredTime =
                                minutesAgo(firstDeliveredAt);

                              if (!tableTime && !kitchenTime && !deliveredTime)
                                return null;

                              return (
                                <div className="mt-1.5 flex flex-col gap-0.5 text-[8px] sm:text-[9px] leading-none w-full items-start px-2">
                                  {tableTime && (
                                    <div className="flex items-center gap-1 text-muted-foreground">
                                      <Clock className="w-2.5 h-2.5 shrink-0 text-muted-foreground/60" />
                                      <span>Mesa: {tableTime}</span>
                                    </div>
                                  )}
                                  {kitchenTime && (
                                    <div className="flex items-center gap-1 text-amber-500 font-medium">
                                      <ChefHat className="w-2.5 h-2.5 shrink-0 text-amber-500/70" />
                                      <span>Cocina: {kitchenTime}</span>
                                    </div>
                                  )}
                                  {deliveredTime && (
                                    <div className="flex items-center gap-1 text-emerald-500 font-medium">
                                      <CheckCircle2 className="w-2.5 h-2.5 shrink-0 text-emerald-500/70" />
                                      <span>Entregado: {deliveredTime}</span>
                                    </div>
                                  )}
                                </div>
                              );
                            })()}

                          {pendingSet.has(table.id) && (
                            <span className="text-[9px] sm:text-[10px] text-amber-500 mt-0.5 font-medium block">
                              Pendiente sync
                            </span>
                          )}
                        </div>
                      ) : (
                        <>
                          <span className="text-[9px] sm:text-[10px] text-muted-foreground mt-0.5 sm:mt-1 font-medium italic block">
                            {table.status === "blocked" && table.blockedReason
                              ? table.blockedReason
                              : getTableStatusLabel(table.status)}
                          </span>
                          {pendingSet.has(table.id) && (
                            <span className="text-[9px] sm:text-[10px] text-amber-500 mt-0.5 font-medium block">
                              Pendiente sync
                            </span>
                          )}
                        </>
                      )}
                    </div>

                    {/* Capacity - Tiny corner badge */}
                    <div className="absolute bottom-2 right-2 flex items-center gap-1 text-muted-foreground/40">
                      <Users className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
                      <span className="text-[8px] sm:text-[10px] font-bold">
                        {table.capacity}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </ScrollArea>
      </div>
    </div>
  );
}
